import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LanguageProvider } from "./i18n/context.tsx";
import { ProjectEditingProvider } from "./ui/editor/context.tsx";
import { translate, type Translate } from "./i18n/hooks.ts";
import { EXAMPLES } from "./model/examples.ts";
import { approximateAreaKm2 } from "./model/gisLayer.ts";
import { defaultCalculationOptions } from "./model/calculationOptions.ts";
import { dateRangeIndices } from "./model/dailySeries.ts";
import { daysBetweenIso } from "./model/isoDate.ts";
import { aemetJobStore } from "./model/aemet/aemetJobStore.ts";
import { isRestartable } from "./model/aemet/aemetRangeMath.ts";
import { driveSyncStore } from "./model/driveSyncStore.ts";
import { saveLanguage, readSavedLanguage, type Language } from "./model/language.ts";
import { saveTheme, readSavedTheme, type Theme } from "./model/theme.ts";
import { validateProject } from "./model/validations.ts";
import { AemetBulkFetchDialog } from "./ui/aemet/AemetBulkFetchDialog.tsx";
import { AemetFetchDialogHost } from "./ui/aemet/AemetFetchDialogHost.tsx";
import { EMPTY_LAYER } from "./ui/appState.ts";
import { ExampleBanner } from "./ui/ExampleBanner.tsx";
import { SaveErrorBanner } from "./ui/SaveErrorBanner.tsx";
import { MitecoAemetBanner } from "./ui/MitecoAemetBanner.tsx";
import { MitecoAttributionBanner } from "./ui/MitecoAttributionBanner.tsx";
import { CalculationOptionsDialog } from "./ui/CalculationOptionsDialog.tsx";
import { EditBar } from "./ui/editor/EditBar.tsx";
import { CalibrationPanel } from "./ui/CalibrationPanel.tsx";
import { calibratorEnabled } from "./model/featureFlags.ts";
import { NameDrawingDialog } from "./ui/editor/NameDrawingDialog.tsx";
import { ChooseEhdDialog } from "./ui/ChooseEhdDialog.tsx";
import { ConfirmCloseWithJobsDialog } from "./ui/ConfirmCloseWithJobsDialog.tsx";
import { UploadExistingProjectsDialog } from "./ui/UploadExistingProjectsDialog.tsx";
import { Landing } from "./ui/Landing.tsx";
import { SubBasinCanvas } from "./ui/SubBasinCanvas.tsx";
import { SubBasinMap } from "./ui/SubBasinMap.tsx";
import { deriveMapModes } from "./ui/deriveMapModes.ts";
import { NewProject } from "./ui/wizard/NewProject.tsx";
import { ResultsPanel } from "./ui/ResultsPanel.tsx";
import { SettingsMenu } from "./ui/SettingsMenu.tsx";
import { useCalculation } from "./ui/useCalculation.ts";
import { useClickOutside } from "./ui/useClickOutside.ts";
import { useEntityCreation } from "./ui/useEntityCreation.ts";
import { usePanels } from "./ui/usePanels.ts";
import { useProjectDataUpdates } from "./ui/useProjectDataUpdates.ts";
import { useProjectPersistence } from "./ui/useProjectPersistence.ts";
import { TopBar } from "./ui/TopBar.tsx";

function yearsInSeries(dates: string[]): number {
  if (dates.length < 2) return 0;
  return daysBetweenIso(dates[0], dates[dates.length - 1]) / 365.25;
}

export default function App() {
  const [language, setLanguage] = useState<Language>(() => readSavedLanguage());
  useEffect(() => {
    saveLanguage(language);
  }, [language]);
  const changeLanguage = useCallback((next: Language) => setLanguage(next), []);
  // For model-layer functions (project.ts, ehd.ts, examples.ts...) that
  // aren't React components and so can't call useT() — same language the rest
  // of the app uses, just applied outside the component tree.
  const t: Translate = useMemo(() => (key, params) => translate(language, key, params), [language]);

  // `state`/`setState` (the whole "currently open project" lifecycle) and
  // every open/save/rename/export/close/delete handler live in this hook —
  // extracted from here for being the single largest, most self-contained
  // slice of what used to be one 1700-line component (see
  // project_evalhid_code_audit.md). Everything else below (calculation,
  // panel toggles, MITECO, drawing, GIS import...) still reads/writes
  // `state`/`setState` directly, same as before.
  const {
    state,
    setState,
    choosingEhd,
    setChoosingEhd,
    confirmCloseWithJobs,
    setConfirmCloseWithJobs,
    savedProjects,
    driveUnsyncedProjects,
    uploadUnsyncedToDrive,
    dismissUnsyncedDriveDialog,
    resolveDriveConflict,
    finishLoadingProject,
    openFolder,
    chooseEhd,
    openSaved,
    renameSaved,
    saveAs,
    renameCurrentProject,
    exportCurrentProject,
    exportCurrentProjectAsCsv,
    importFromFile,
    deleteCurrentProject,
    closeProject,
    requestCloseProject,
    requestImmediateSave,
    retrySave,
    loadExample,
  } = useProjectPersistence(t, language);

  // The three ways a new sub-basin/outlet point/aquifer gets added to this
  // project (MITECO, GIS file import, hand-drawn) — see the hook's own
  // header comment for why these three specifically are grouped together.
  const {
    toggleMitecoMode,
    changeMitecoLayer,
    changeMitecoSelected,
    addSelectedMiteco,
    recoverSubBasinsFromGis,
    createOutletPointsFromGis,
    createAquifersFromGis,
    startDrawing,
    cancelDrawing,
    completeDrawing,
    confirmDrawingName,
  } = useEntityCreation(t, setState);

  // Warns on an actual browser tab close/refresh too, not just our own
  // "Close project" button — the only warning the browser lets a page show
  // is its own generic one (custom text in `returnValue` is ignored by
  // every modern browser), triggered just by calling preventDefault().
  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (aemetJobStore.hasActiveJobs()) {
        e.preventDefault();
        e.returnValue = "";
      }
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // Auto-hides MitecoAemetBanner.tsx once its background fetch (started/
  // queued by the MITECO wizard tab, see `showMitecoAemetBanner` above) has
  // nothing left to do — `isRestartable` covers every finished state
  // (applied, errored, no stations), so this only fires once every job the
  // batch queued, including ones still "pendiente" and not yet started, has
  // actually settled. Only subscribes while the banner is showing, and only
  // ever flips it false — never resurrects a banner the user already
  // dismissed by hand.
  const mitecoAemetBannerVisible = state.phase === "loaded" && state.mitecoAemetBannerVisible;
  useEffect(() => {
    if (!mitecoAemetBannerVisible) return;
    const checkDone = () => {
      if (aemetJobStore.getAllJobs().every((job) => isRestartable(job.phase))) {
        setState((current) =>
          current.phase === "loaded" ? { ...current, mitecoAemetBannerVisible: false } : current
        );
      }
    };
    checkDone();
    return aemetJobStore.subscribe(checkDone);
  }, [mitecoAemetBannerVisible, setState]);

  const [theme, setTheme] = useState<Theme>(() => readSavedTheme());
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    saveTheme(theme);
  }, [theme]);
  const toggleTheme = useCallback(() => {
    setTheme((current) => (current === "light" ? "dark" : "light"));
  }, []);

  // The Landing/wizard screens' own SettingsMenu (see TopBar.tsx for the
  // loaded-project one) — nothing else there to coordinate "only one open"
  // with, so it just needs its own local open state + click-outside.
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const floatingControlsRef = useRef<HTMLDivElement>(null);
  useClickOutside(floatingControlsRef, () => setSettingsMenuOpen(false), settingsMenuOpen);
  // The "upload existing projects?" dialog (UploadExistingProjectsDialog.tsx)
  // shows up right after connecting Drive — almost always while this same
  // menu is still open, since that's exactly where the "Connect" button
  // lives. Left open, its dropdown (taller than just the gear button, see
  // Dialog.tsx's `anchor="top-right"`) would overlap the new dialog instead
  // of sitting cleanly below it.
  useEffect(() => {
    if (driveUnsyncedProjects) setSettingsMenuOpen(false);
  }, [driveUnsyncedProjects]);

  const { runCalculation, openCalculationDialog, closeCalculationDialog, recalculate } =
    useCalculation(t, state, setState);

  const {
    activePanel,
    setActivePanel,
    zoneAssignMode,
    setZoneAssignMode,
    resultsLevel,
    setResultsLevel,
    openEditorPanel,
    toggleResultsPanel,
    toggleEditorPanel,
    toggleCalibrationPanel,
    openBulkAemetFetch,
    closeBulkAemetFetch,
    changeMapSelection,
    selectSeries,
    toggleMapView,
    highlightedSubBasin,
    selectSubBasinFromSearch,
  } = usePanels(setState);

  const {
    updateProject,
    updateGisLayer,
    updateOutletPointsLayer,
    updateAquifersLayer,
    updateGenericLayers,
    updateObservedFlow,
    recordAemetProvenance,
    updateSeries,
    selectResult,
    onAemetApplySuccess,
    flushAemetApply,
  } = useProjectDataUpdates(setState);

  // The stable project-data slice EditBar.tsx and its editor-only tabs read
  // via useProjectEditing() instead of as explicit props — see
  // ProjectEditingProvider (ui/editor/context.tsx). Computed unconditionally
  // (null while no project is loaded) because it's a Hook call — the actual
  // JSX below only ever renders it once state.phase === "loaded" anyway
  // (state.editorPanelOpen only exists in that phase, see appState.ts).
  const projectEditingValue = useMemo(
    () =>
      state.phase === "loaded"
        ? {
            project: state.loadedProject.project,
            onChangeProject: updateProject,
            gisLayer: state.gisLayer,
            onChangeGisLayer: updateGisLayer,
            outletPointsLayer: state.outletPointsLayer,
            onChangeOutletPointsLayer: updateOutletPointsLayer,
            aquifersLayer: state.aquifersLayer,
            onChangeAquifersLayer: updateAquifersLayer,
            series: state.loadedProject.series,
            onChangeSeries: updateSeries,
            aemetProvenance: state.aemetProvenance,
            onRecordAemetProvenance: recordAemetProvenance,
            genericLayers: state.genericLayers,
            onChangeGenericLayers: updateGenericLayers,
          }
        : null,
    [
      state,
      updateProject,
      updateGisLayer,
      updateOutletPointsLayer,
      updateAquifersLayer,
      updateSeries,
      recordAemetProvenance,
      updateGenericLayers,
    ]
  );

  // No project open (Landing/loading/error/creating) — see the real
  // registration further down, once state.phase === "loaded" is confirmed.
  if (state.phase !== "loaded") {
    aemetJobStore.setContext(null);
  }

  if (state.phase === "creating") {
    return (
      <LanguageProvider language={language}>
        <div className="floating-controls" ref={floatingControlsRef}>
          <SettingsMenu
            theme={theme}
            onToggleTheme={toggleTheme}
            language={language}
            onChangeLanguage={changeLanguage}
            open={settingsMenuOpen}
            onToggle={() => setSettingsMenuOpen((open) => !open)}
            onClose={() => setSettingsMenuOpen(false)}
          />
        </div>
        <NewProject
          onCancel={() => setState({ phase: "initial" })}
          onComplete={(loadedProject, gisLayer, name, miteco) => {
            finishLoadingProject(
              loadedProject,
              gisLayer,
              name,
              false,
              miteco?.mitecoOrigins,
              miteco != null && miteco.aemetRequests.length > 0
            );
            if (miteco && miteco.aemetRequests.length > 0) {
              // aemetJobStore has no project/series to apply against until
              // the render above actually commits (state.phase !== "loaded"
              // nulls its context, see the unconditional setContext further
              // down) — set it explicitly here with the fresh values instead
              // of waiting a render, same reasoning as that call's own
              // comment.
              aemetJobStore.setContext({
                project: loadedProject.project,
                series: loadedProject.series,
                aemetProvenance: {},
                t,
                onApplySuccess: onAemetApplySuccess,
                onBatchStart: driveSyncStore.pauseSync,
                onBatchEnd: () => {
                  flushAemetApply();
                  requestImmediateSave();
                  driveSyncStore.resumeSync();
                },
              });
              void aemetJobStore.startQueuedWithRetry(miteco.aemetRequests).then(() => {
                // Real gap `startQueuedWithRetry` alone can't close: it only
                // ever re-checks the requests it was GIVEN — but
                // Step1SubBasins.tsx's own aemetRequests builder silently
                // drops any sub-basin whose centroid comes back null at
                // wizard time, so such a sub-basin was never queued at all.
                // This final sweep re-derives centroid/geometry against the
                // live gisLayer for EVERY sub-basin still missing data, not
                // just the ones already tried — see
                // sweepMissingSubBasins's own comment.
                const firstOpts = miteco.aemetRequests[0]?.opts;
                if (gisLayer && firstOpts?.method && firstOpts.freeStart && firstOpts.freeEnd) {
                  void aemetJobStore.sweepMissingSubBasins(gisLayer, firstOpts.method, {
                    start: firstOpts.freeStart,
                    end: firstOpts.freeEnd,
                  });
                }
              });
            }
          }}
        />
      </LanguageProvider>
    );
  }

  if (state.phase !== "loaded") {
    return (
      <LanguageProvider language={language}>
        <div className="floating-controls" ref={floatingControlsRef}>
          <SettingsMenu
            theme={theme}
            onToggleTheme={toggleTheme}
            language={language}
            onChangeLanguage={changeLanguage}
            open={settingsMenuOpen}
            onToggle={() => setSettingsMenuOpen((open) => !open)}
            onClose={() => setSettingsMenuOpen(false)}
          />
        </div>
        <Landing
          loading={state.phase === "loading"}
          error={state.phase === "error" ? state.message : null}
          savedProjects={savedProjects}
          onOpen={openFolder}
          onCreateNew={() => setState({ phase: "creating" })}
          onOpenSaved={openSaved}
          onRenameSaved={renameSaved}
          onImport={importFromFile}
          onResolveDriveConflict={resolveDriveConflict}
          examples={EXAMPLES}
          onLoadExample={loadExample}
        />
        {choosingEhd && (
          <ChooseEhdDialog
            found={choosingEhd}
            onChoose={chooseEhd}
            onClose={() => setChoosingEhd(null)}
          />
        )}
        {driveUnsyncedProjects && (
          <UploadExistingProjectsDialog
            projects={driveUnsyncedProjects}
            onUploadAll={uploadUnsyncedToDrive}
            onClose={dismissUnsyncedDriveDialog}
          />
        )}
      </LanguageProvider>
    );
  }

  // Registered on every render (a plain field assignment on the module-level
  // store, not a state update) so any AEMET job's download/apply logic
  // always reads/writes the CURRENT project/series — even one started for a
  // sub-basin whose own EditSubBasinDialog.tsx has long since closed. See
  // aemetJobStore.ts's header comment.
  aemetJobStore.setContext({
    project: state.loadedProject.project,
    series: state.loadedProject.series,
    aemetProvenance: state.aemetProvenance,
    t,
    onApplySuccess: onAemetApplySuccess,
    onBatchStart: driveSyncStore.pauseSync,
    onBatchEnd: () => {
      flushAemetApply();
      // Real data-loss bug found live: a long batch (Duero: 1728 sub-basins)
      // keeps resetting the debounced autosave's 1s timer for as long as
      // applies keep arriving — nothing reaches IndexedDB for the whole
      // duration, and extending series, then calculating, then refreshing
      // shortly after (as reported) can lose it all even though the batch
      // itself finished successfully. `flushAemetApply()` above only gets
      // the final data into React state; this makes the very next autosave
      // run skip its wait instead — see requestImmediateSave's own comment
      // in useProjectPersistence.ts.
      requestImmediateSave();
      driveSyncStore.resumeSync();
    },
  });

  // The full loaded precipitation range, narrowed to whatever range the
  // CURRENT `results` were actually calculated over — `calculationOptions`
  // is only ever set together with `results` (withCalculationSucceeded,
  // useCalculation.ts), so they're always in sync; falls back to the full
  // range before any calculation has run (calculationOptions is null then).
  const fullDates = state.loadedProject.series.precipitation?.dates ?? [];
  const calculatedRange = state.calculationOptions
    ? dateRangeIndices(
        fullDates,
        state.calculationOptions.startDate,
        state.calculationOptions.endDate
      )
    : null;
  const dates = calculatedRange
    ? fullDates.slice(calculatedRange.start, calculatedRange.end + 1)
    : fullDates;
  const validation = validateProject(t, state.loadedProject.project, state.loadedProject.series);
  const { selectionMode, resultsMode, seriesMode, mitecoMode, colorMode } = deriveMapModes(
    state,
    activePanel,
    zoneAssignMode,
    resultsLevel,
    { changeMapSelection, selectResult, selectSeries, changeMitecoSelected }
  );

  const pendingDrawing = state.pendingDrawing;
  const suggestedDrawingName = pendingDrawing
    ? {
        subBasin: t("projectEntities.defaultSubBasinName", {
          n: state.loadedProject.project.subBasins.length + 1,
        }),
        outletPoint: t("projectEntities.defaultOutletPointName", {
          n: state.loadedProject.project.outletPoints.length + 1,
        }),
        aquifer: t("projectEntities.defaultAquiferName", {
          n: state.loadedProject.project.aquifers.length + 1,
        }),
      }[pendingDrawing.tool]
    : "";

  return (
    <LanguageProvider language={language}>
      <div className="app-shell">
        <TopBar
          loadedProject={state.loadedProject}
          validation={validation}
          calculationError={state.calculationError}
          saveError={state.saveError}
          invalidDataWarning={state.invalidDataWarning}
          resultsAvailable={state.results !== null}
          resultsPanelOpen={state.resultsPanelOpen}
          editorPanelOpen={state.editorPanelOpen}
          calibrationPanelOpen={state.calibrationPanelOpen}
          showCalibrationButton={calibratorEnabled()}
          mapView={state.mapView}
          calculationOptionsReady={state.calculationOptions !== null}
          calculationProgress={state.calculationProgress}
          theme={theme}
          language={language}
          onToggleResults={toggleResultsPanel}
          onOpenEditorPanel={openEditorPanel}
          onToggleCalibrationPanel={toggleCalibrationPanel}
          onToggleMapView={toggleMapView}
          onOpenCalculationOptions={openCalculationDialog}
          onRecalculate={recalculate}
          onToggleTheme={toggleTheme}
          onChangeLanguage={changeLanguage}
          projectName={state.name}
          onCloseProject={() => {
            // Guarantee any buffered AEMET/ROCIO apply (see
            // useProjectDataUpdates.ts's AemetApplyBuffer) is in React
            // state before requestCloseProject() reads it for the
            // immediate save — otherwise up to AEMET_APPLY_THROTTLE_MS of
            // just-downloaded data could close unsaved.
            flushAemetApply();
            requestCloseProject();
          }}
          isEphemeral={state.ephemeral}
          onSaveAs={saveAs}
          onRename={renameCurrentProject}
          onExport={exportCurrentProject}
          onExportCsv={exportCurrentProjectAsCsv}
          onUpdateProject={updateProject}
          onDeleteProject={deleteCurrentProject}
          onSelectSubBasin={selectSubBasinFromSearch}
        />
        <div className="workspace">
          {state.mapView ? (
            <SubBasinMap
              layer={state.gisLayer ?? EMPTY_LAYER}
              outletPointsLayer={state.outletPointsLayer}
              aquifersLayer={state.aquifersLayer}
              project={state.loadedProject.project}
              series={state.loadedProject.series}
              selectionMode={selectionMode}
              drawingMode={state.drawingMode}
              resultsMode={resultsMode}
              seriesMode={seriesMode}
              editorPanel={activePanel}
              onCompletarDibujo={completeDrawing}
              onCancelDrawing={cancelDrawing}
              onChangeProject={updateProject}
              onChangeSeries={updateSeries}
              mitecoMode={mitecoMode}
              aemetProvenance={state.aemetProvenance}
              onRecordAemetProvenance={recordAemetProvenance}
              observedFlow={state.observedFlow}
              onChangeObservedFlow={updateObservedFlow}
              calibrationMode={state.calibrationPanelOpen}
              colorMode={colorMode}
              genericLayers={state.genericLayers}
              onChangeGenericLayers={updateGenericLayers}
              isEphemeral={state.ephemeral}
              highlightedSubBasinCode={highlightedSubBasin}
            />
          ) : (
            <SubBasinCanvas
              project={state.loadedProject.project}
              series={state.loadedProject.series}
              gisLayer={state.gisLayer}
              selectionMode={selectionMode}
              seriesMode={seriesMode}
              editorPanel={activePanel}
              onChangeProject={updateProject}
              onChangeSeries={updateSeries}
              aemetProvenance={state.aemetProvenance}
              onRecordAemetProvenance={recordAemetProvenance}
              observedFlow={state.observedFlow}
              onChangeObservedFlow={updateObservedFlow}
              calibrationMode={state.calibrationPanelOpen}
            />
          )}

          {state.editorPanelOpen && projectEditingValue && (
            <ProjectEditingProvider value={projectEditingValue}>
              <EditBar
                selection={state.mapSelection}
                onChangeSelection={changeMapSelection}
                seriesSelection={state.seriesSelection}
                onChangeSeriesSelection={selectSeries}
                mitecoMode={mitecoMode}
                onToggleMitecoMode={toggleMitecoMode}
                onChangeMitecoLayer={changeMitecoLayer}
                onAddSelectedMiteco={addSelectedMiteco}
                onRecoverFromGis={recoverSubBasinsFromGis}
                onCreateOutletPointsFromGis={createOutletPointsFromGis}
                onCreateAquifersFromGis={createAquifersFromGis}
                onClose={toggleEditorPanel}
                onOpenBulkAemet={openBulkAemetFetch}
                panel={activePanel}
                onChangePanel={setActivePanel}
                zoneAssignMode={zoneAssignMode}
                onChangeZoneAssignMode={setZoneAssignMode}
                drawingMode={state.drawingMode}
                onStartDrawing={startDrawing}
                onCancelDrawing={cancelDrawing}
              />
            </ProjectEditingProvider>
          )}

          {state.calibrationPanelOpen && (
            <CalibrationPanel
              project={state.loadedProject.project}
              series={state.loadedProject.series}
              observedFlow={state.observedFlow}
              onChangeProject={updateProject}
              onChangeObservedFlow={updateObservedFlow}
              onClose={toggleCalibrationPanel}
            />
          )}

          {state.results && state.resultsPanelOpen && (
            <ResultsPanel
              results={state.results}
              project={state.loadedProject.project}
              onChangeProject={updateProject}
              dates={dates}
              years={yearsInSeries(dates)}
              pumpingSeries={state.loadedProject.series.pumping}
              series={state.loadedProject.series}
              calculationOptions={state.calculationOptions}
              selection={state.resultSelection}
              onSelect={selectResult}
              onLevelChange={setResultsLevel}
              onClose={toggleResultsPanel}
            />
          )}

          {state.saveError && <SaveErrorBanner message={state.saveError} onRetry={retrySave} />}
          {state.ephemeral && (
            <ExampleBanner
              projectName={state.name}
              onSaveAsCopy={saveAs}
              aboveDrawingBar={Boolean(state.drawingMode)}
            />
          )}
          {state.mitecoAemetBannerVisible && (
            <MitecoAemetBanner
              onDismiss={() =>
                setState((current) =>
                  current.phase === "loaded"
                    ? { ...current, mitecoAemetBannerVisible: false }
                    : current
                )
              }
            />
          )}
          {mitecoMode && <MitecoAttributionBanner layer={mitecoMode.layer} />}
          <AemetFetchDialogHost series={state.loadedProject.series} />
          {state.bulkAemetOpen && (
            <AemetBulkFetchDialog
              project={state.loadedProject.project}
              series={state.loadedProject.series}
              gisLayer={state.gisLayer}
              aemetProvenance={state.aemetProvenance}
              onlySubBasinCodes={state.bulkAemetOnlyCodes}
              onClose={closeBulkAemetFetch}
            />
          )}
        </div>

        {state.calculationDialogOpen && state.loadedProject.series.precipitation && (
          <CalculationOptionsDialog
            precipitation={state.loadedProject.series.precipitation}
            initialValues={
              state.calculationOptions ??
              defaultCalculationOptions(
                state.loadedProject.project,
                state.loadedProject.series.precipitation
              )
            }
            onConfirm={runCalculation}
            onClose={closeCalculationDialog}
          />
        )}

        {pendingDrawing && (
          <NameDrawingDialog
            tool={pendingDrawing.tool}
            suggestedName={suggestedDrawingName}
            suggestedAreaKm2={
              pendingDrawing.tool === "subBasin" && pendingDrawing.geometry.type === "Polygon"
                ? approximateAreaKm2(pendingDrawing.geometry.coordinates[0] as [number, number][])
                : undefined
            }
            onConfirm={confirmDrawingName}
            onCancel={cancelDrawing}
          />
        )}

        {confirmCloseWithJobs && (
          <ConfirmCloseWithJobsDialog
            onConfirm={() => {
              aemetJobStore.cancelAll();
              flushAemetApply();
              closeProject();
            }}
            onClose={() => setConfirmCloseWithJobs(false)}
          />
        )}
      </div>
    </LanguageProvider>
  );
}
